import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Plus, ChevronDown } from 'lucide-react';
import { expertiseTagsAPI } from '../api/client';
import type { ExpertiseTag } from '../types';

interface TagPickerProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  accentColor?: string;
  label?: string;
  // 'expertise' (default) keeps the original onboarding behavior: it fetches
  // the shared expertise-tag vocabulary and creates a new expertise tag for any
  // novel entry. 'free' is a plain free-tag mode that never touches the
  // expertise-tag API — used for content categories, which must not pollute the
  // expertise vocabulary.
  mode?: 'expertise' | 'free';
  // Optional local vocabulary shown in the dropdown when mode is 'free'.
  suggestions?: string[];
}

/** Tag list after a picker key. Backspace never removes a saved tag. */
export function tagsAfterKey(key: string, input: string, selectedTags: string[]): string[] {
  if (key === 'Enter' || key === ',') {
    const trimmed = input.trim();
    if (!trimmed || selectedTags.includes(trimmed)) return selectedTags;
    return [...selectedTags, trimmed];
  }
  return selectedTags;
}

const CATEGORY_LABELS: Record<string, string> = {
  business: 'Business & Industry',
  skills: 'Skills & Functions',
  faith: 'Faith & Personal',
  trades: 'Trades & Services',
};

export default function TagPicker({
  selectedTags,
  onChange,
  placeholder = 'Search or type to add...',
  accentColor = 'navy',
  label,
  mode = 'expertise',
  suggestions: freeVocabulary = [],
}: TagPickerProps) {
  const isFree = mode === 'free';
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<ExpertiseTag[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [allTags, setAllTags] = useState<ExpertiseTag[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isFree) return;
    expertiseTagsAPI.getAll().then(setAllTags).catch(() => {});
  }, [isFree]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = useCallback(
    async (query: string) => {
      // Free-tag mode never calls the expertise API and keeps the (typed)
      // expertise `suggestions` state empty; its dropdown is rendered from the
      // optional local vocabulary computed in render.
      if (isFree) return;
      if (!query.trim()) {
        setSuggestions(allTags.filter((t) => !selectedTags.includes(t.name)));
        return;
      }
      setLoading(true);
      try {
        const results = await expertiseTagsAPI.getAll({ search: query });
        setSuggestions(results.filter((t) => !selectedTags.includes(t.name)));
      } catch {
        setSuggestions(
          allTags
            .filter(
              (t) =>
                t.name.toLowerCase().includes(query.toLowerCase()) &&
                !selectedTags.includes(t.name)
            )
        );
      } finally {
        setLoading(false);
      }
    },
    [allTags, selectedTags, isFree]
  );

  // Free-mode dropdown entries: filter the caller-supplied vocabulary (if any)
  // by the current input, excluding already-selected tags.
  const freeMatches = isFree
    ? (input.trim()
        ? freeVocabulary.filter((v) => v.toLowerCase().includes(input.trim().toLowerCase()))
        : freeVocabulary
      ).filter((v) => !selectedTags.includes(v))
    : [];

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showDropdown) search(input);
    }, 200);
    return () => clearTimeout(timer);
  }, [input, showDropdown, search]);

  const addTag = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || selectedTags.includes(trimmed)) return;
    onChange([...selectedTags, trimmed]);
    setInput('');
    // Free-tag mode never writes to the shared expertise vocabulary.
    if (isFree) return;
    if (!allTags.some((t) => t.name.toLowerCase() === trimmed.toLowerCase())) {
      try {
        const newTag = await expertiseTagsAPI.create(trimmed);
        setAllTags((prev) => [...prev, newTag]);
      } catch {
        // Tag creation failed silently -- the string is still saved to the user's profile
      }
    }
  };

  const removeTag = (index: number) => {
    onChange(selectedTags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (tagsAfterKey(e.key, input, selectedTags) !== selectedTags) {
        addTag(input);
      }
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const groupedSuggestions = suggestions.reduce<Record<string, ExpertiseTag[]>>(
    (acc, tag) => {
      const cat = tag.category || 'skills';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(tag);
      return acc;
    },
    {}
  );

  const bgLight = accentColor === 'gold' ? 'bg-gold/10' : 'bg-off-white';
  const textColor = accentColor === 'gold' ? 'text-gold' : 'text-navy';

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}

      <div className="flex flex-wrap gap-2 mb-2">
        {selectedTags.map((tag, idx) => (
          <span
            key={idx}
            className={`inline-flex items-center gap-1 px-3 py-1.5 ${bgLight} ${textColor} text-sm rounded-full`}
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(idx)}
              className="hover:text-red-500 transition"
            >
              <X size={14} />
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => {
              setShowDropdown(true);
              search(input);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20 pr-8"
          />
          <button
            type="button"
            onClick={() => {
              setShowDropdown(!showDropdown);
              if (!showDropdown) search(input);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <ChevronDown size={16} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => addTag(input)}
          className={`px-3 py-2 ${bgLight} ${textColor} rounded-lg hover:opacity-80 transition`}
        >
          <Plus size={18} />
        </button>
      </div>

      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
          )}
          {!loading && Object.keys(groupedSuggestions).length === 0 && input.trim() && (
            <button
              type="button"
              onClick={() => addTag(input)}
              className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50"
            >
              Add custom tag: <span className="font-medium">"{input.trim()}"</span>
            </button>
          )}
          {!loading && isFree &&
            freeMatches.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => {
                  addTag(name);
                  setShowDropdown(false);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
              >
                {name}
              </button>
            ))}
          {!loading && !isFree &&
            Object.entries(groupedSuggestions).map(([category, tags]) => (
              <div key={category}>
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 sticky top-0">
                  {CATEGORY_LABELS[category] || category}
                </div>
                {tags.slice(0, 10).map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => {
                      addTag(tag.name);
                      setShowDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                  >
                    <span>{tag.name}</span>
                    {!tag.is_predefined && (
                      <span className="text-xs text-gray-400">custom</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          {!loading && suggestions.length === 0 && !input.trim() && (
            <div className="px-4 py-3 text-sm text-gray-500">
              {isFree ? 'Type a keyword and press Enter to add' : 'Start typing to search expertise tags'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
